/***
 *
 *   FORM
 *   Self-validating form that accepts an object for construction
 *   Read the full documentation on object formatting
 *   https://docs.usegravity.app/ui/form
 *
 *   PROPS
 *   data: the object containing your form data
 *   callback: function to be executed on successful submit
 *   url: url to send the form to (optional)
 *   method: HTTP request type
 *   redirect: url to redirect to after a successful submit (optional)
 *   buttonText: submit button text
 *   cancel: true/false to toggle a cancel button (optional)
 *
 **********/

import { useState, useEffect, useContext, useRef } from 'react';
import Axios from 'axios';
import { getApiErrorMessage } from 'api/apiClient';

import {
  FormHeader,
  TextInput,
  EmailInput,
  URLInput,
  PhoneInput,
  DateInput,
  PasswordInput,
  HiddenInput,
  CardInput,
  Select,
  FormLink,
  Switch,
  FileInput,
  Fieldset,
  ViewContext,
  useNavigate,
  ClassHelper,
  Grid,
} from 'components/lib';

import type React from 'react';
import Style from './form.tailwind.js';
import { UMButton, UMInputNumber } from '@urbanmetrix/um-kit';
import type { UMSharedIconProps } from '@urbanmetrix/um-kit';

interface FormFile {
  name?: string;
  type?: string;
  size?: number;
  data?: unknown;
  [key: string]: unknown;
}

type FormDataMap = Record<string, FormInputDefinition>;
// fileStore is initialised as an array but is also indexed by input name at runtime
type FileStore = FormFile[] & Record<string, FormFile[]>;

interface FormApiError {
  response?: {
    data?: { inputError?: string; msg?: string; message?: string };
  };
}

interface FormInputDefinition {
  type?: string;
  value?: unknown;
  valid?: boolean;
  default?: unknown;
  required?: boolean;
  errorMessage?: string;
  cols?: number;
  style?: React.CSSProperties;
  width?: number | string;
  label?: string;
  class?: string;
  labelTooltip?: string;
  min?: number;
  max?: number;
  options?: unknown;
  selectAll?: unknown;
  url?: string;
  text?: string;
  title?: string;
  accept?: string[];
  description?: string;
  readonly?: boolean;
  maxFileSize?: number;
  handleLabel?: string;
  placeholder?: string;
  defaultValue?: unknown;
  complexPassword?: boolean;
  name?: string;
}

interface FormProps {
  data: Record<string, FormInputDefinition>;
  callback?: (res: unknown) => void | Promise<void>;
  url?: string;
  method?: string;
  redirect?: string;
  buttonText?: string;
  cancel?: () => void;
  isContainFile?: boolean;
  updateOnChange?: boolean;
  onChange?: (args: { input: string; value: unknown; valid?: boolean }) => void;
  fullWidthButton?: boolean;
  name?: string;
  validateOnBlur?: boolean;
  errorResetDelay?: number;
  onSubmitStart?: () => void;
  onSubmitEnd?: (success: boolean) => void;
  beforeSubmit?: (args: {
    form: Record<string, FormInputDefinition>;
    submitAction?: string;
  }) => boolean | Promise<boolean>;
  customSubmit?: (data: unknown) => unknown | Promise<unknown>;
  submitOnChange?: boolean;
  toastSuccess?: boolean;
  errorAutoclose?: boolean;
  toastError?: boolean;
  loading?: boolean;
  action?: string;
  preSubmitText?: React.ReactNode;
  preSubmitClassName?: string;
  preSubmitStyle?: React.CSSProperties;
  alignRight?: boolean;
  icon?: React.ComponentType<UMSharedIconProps> | string;
  onboardingStyle?: string;
  buttonClassName?: string;
  disableModalYesClass?: boolean;
  customClassName?: string;
  buttonColor?: string;
  buttonVariant?: 'text' | 'contained' | 'outlined';
  disabled?: boolean;
  destructive?: boolean;
  className?: string;
  submitAction?: string;
}

function Form(props: FormProps) {
  // context & state
  const context = useContext(ViewContext);
  const [form, setForm] = useState<FormDataMap | null>(null);
  const [loading, setLoading] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const loadingRef = useRef(false);
  const errorResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevSerializedDataRef = useRef<string | null>(null);
  // This line was changed by Huseyin
  // const [fileStore, setFileStore] = useState([]);
  const [fileStore, setFileStore] = useState<FileStore>(
    props.isContainFile ? ([{}] as unknown as FileStore) : ([] as unknown as FileStore)
  );

  const [processCreditCard, setProcessCreditCard] = useState(false);
  const navigate = useNavigate();
  let valid = true;

  // inputs map
  const Inputs = {
    text: TextInput,
    textarea: TextInput,
    email: EmailInput,
    number: (inputProps: React.ComponentProps<typeof UMInputNumber>) => <UMInputNumber width={84} {...inputProps} />,
    url: URLInput,
    date: DateInput,
    hidden: HiddenInput,
    phone: PhoneInput,
    password: PasswordInput,
    creditcard: CardInput,
    radio: Fieldset,
    select: Select,
    checkbox: Fieldset,
    selector: Fieldset,
    switch: Switch,
    header: FormHeader,
    link: FormLink,
    file: FileInput,
  };

  useEffect(() => {
    const serializedData = JSON.stringify(props.data);
    const propsDataChanged = prevSerializedDataRef.current !== serializedData;

    if (!form) {
      let data = { ...props.data };

      // init credit card
      if (data?.token) {
        data?.plan?.default === 'free' ? setProcessCreditCard(false) : setProcessCreditCard(true);
      }

      setForm(data);
      prevSerializedDataRef.current = serializedData;
      return;
    }

    if (valid && (props.updateOnChange || propsDataChanged)) {
      setForm(props.data);
    }

    prevSerializedDataRef.current = serializedData;
  }, [props.data, props.updateOnChange]);

  if (!form) return false;
  const shouldUseDefaultButtonClass = props.fullWidthButton !== false;
  const buttonLoading = loading || Boolean(props.loading);

  function update(input: string, value: unknown, valid?: boolean) {
    if (props.onChange) props.onChange({ input, value, valid });
    let data: FormDataMap = { ...form };

    // is it a file?
    const fileValue = value as FormFile[] | undefined;
    if (fileValue?.length && fileValue[0].name && fileValue[0].type && fileValue[0].size) {
      if (!fileStore[input]?.length) fileStore[input] = [];

      const newFiles = { ...fileStore } as FileStore;
      fileValue.forEach((file) => {
        // add or delete the file
        if (file.data && !fileStore[input].find((x) => x.name === file.name)) {
          newFiles[input].push(file);
        } else if (!file.data) {
          newFiles[input].splice(
            newFiles[input].findIndex((x) => x.name === file.name),
            1
          );
        }
      });

      data[input].value = newFiles[input];
      data[input].valid = valid;
      setFileStore(newFiles);
    } else {
      // update input value & valid state
      data[input].value = value;
      data[input].valid = valid;

      // hide credit card input when selecting free plan
      if (props.data.token) {
        if (input === 'plan' && value === 'free') {
          setProcessCreditCard(false);
        } else if (input === 'plan' && value !== 'free') {
          setProcessCreditCard(true);
        }
      }
    }
    setForm(data);
    props.updateOnChange && props.onChange?.({ input: input, value: value, valid: valid });

    props.submitOnChange && submit();
  }

  function validate() {
    // loop over each input and check it's valid
    // show error if input is requured and value is
    // blank, input validation will be executed on blur

    let errors: boolean[] = [];
    let data: FormDataMap = { ...form };

    // loop the inputs
    for (let input in data) {
      // validate credit card
      if (input === 'token') {
        if (processCreditCard && (data.token.value as { error?: unknown } | undefined)?.error) {
          data.token.valid = false;
          errors.push(false);
        } else {
          data.token.valid = true;
        }
      } else {
        // standard input
        let inp = data[input];
        if (inp.value === undefined && inp.default) {
          data[input].value = inp.default;
        }

        if (inp.required) {
          if (!inp.value || inp.value === 'unselected') {
            inp.valid = false;
            errors.push(false);
          }
        }

        if (inp.valid === false) {
          errors.push(false);
        }
      }
    }

    if (errors.length) {
      // form isn't valid
      valid = false;
      setForm(data);
      setShowErrors(true);
      if (props.errorResetDelay) {
        if (errorResetTimer.current) clearTimeout(errorResetTimer.current);
        errorResetTimer.current = setTimeout(() => {
          setShowErrors(false);
        }, props.errorResetDelay);
      }
      return false;
    } else {
      // form is valid
      setShowErrors(false);
      return true;
    }
  }

  async function submit(event?: React.FormEvent | React.MouseEvent) {
    if (event?.preventDefault) {
      event.preventDefault();
    }
    if (loadingRef.current) return;

    loadingRef.current = true;
    props.onSubmitStart && props.onSubmitStart();
    setLoading(true);

    let data: Record<string, unknown> | FormData = { ...form };
    let success = false;

    try {
      // is the form valid?
      if (!validate()) {
        return false;
      }

      if (props.beforeSubmit) {
        const canSubmit = await props.beforeSubmit({
          form: data as FormDataMap,
          submitAction: props.submitAction,
        });
        if (!canSubmit) {
          return false;
        }
      }

      // optimise data for server
      const valueData = data as Record<string, unknown>;
      for (let input in form) {
        if (processCreditCard && input === 'token') {
          // procress credit card
          valueData[input] = form[input].value;
        } else if (input !== 'header') {
          // process single input & ignore headers
          valueData[input] = form[input].value;
        }
      }

      delete valueData.header;

      // submit the form or execute callback
      if (!props.url && !props.customSubmit) {
        if (props.callback) await props.callback(null);
        success = true;
        return false;
      }

      let formData = new FormData(),
        headers: Record<string, string> = {};
      if (Object.keys(fileStore).length) {
        headers['Content-Type'] = 'multipart/form-data';
        headers['Accept'] = 'application/json';

        for (let key in valueData) {
          const fieldValue = valueData[key];
          // append files
          if (Array.isArray(fieldValue) && fieldValue[0].hasOwnProperty('data')) {
            for (let i = 0; i < fieldValue.length; i++) {
              formData.append(key, fieldValue[i].data);
            }
          } else {
            // append text values
            formData.append(key, fieldValue as string | Blob);
          }
        }

        data = formData;
      }

      let res;

      if (props.customSubmit) {
        const responseData = await props.customSubmit(data);
        res = { data: responseData };
      } else {
        res = await Axios({
          method: props.method,
          url: props.url,
          data: data,
        });
      }

      // close the modal
      context.modal.hide(false, res.data?.data ?? res.data);

      // callback?
      if (props.callback) await props.callback(res);

      // redirect?
      if (props.redirect) navigate(props.redirect);

      // success notification
      if (props.toastSuccess !== false && res.data?.message) {
        context.notification.show(res.data.message, 'success', true, 'toast');
      }
      success = true;
      return true;
    } catch (err) {
      context.modal.hide(true);

      // show error on input
      const apiErr = err as FormApiError;
      if (apiErr.response?.data?.inputError) {
        let data: FormDataMap = { ...form };
        const input = apiErr.response.data.inputError;
        data[input].valid = false;
        data[input].errorMessage = apiErr.response.data?.msg || apiErr.response.data?.message;
        valid = false;
        setForm(data);
        return false;
      } else {
        // general errors handled by view
        context.handleError(err, { autoclose: props.errorAutoclose });
        if (props.toastError) context.notification.show(getApiErrorMessage(err), 'error', true, 'toast');
        return false;
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
      props.onSubmitEnd && props.onSubmitEnd(success);
    }
  }

  let inputsToRender: FormInputDefinition[] = [];
  const formStyle = ClassHelper(Style, {
    ...props,
    ...{
      loading: props.loading || loading,
    },
  });

  // map the inputs
  Object.keys(form).map((name) => {
    // get the values for this input
    const data = form[name];
    data.name = name;
    inputsToRender.push(data);
    return inputsToRender;
  });

  // render the form
  return (
    <form
      action={props.action}
      method={props.method}
      onSubmit={submit}
      className={formStyle}
      encType={fileStore.length ? 'multipart/form-data' : undefined}
      noValidate
    >
      <Grid>
        {inputsToRender.map((input) => {
          if (input.type === null) return false;

          if (!input.type) input.type = 'text';

          if (input.type === 'creditcard' && !processCreditCard) return false;

          const Input = Inputs[input.type as keyof typeof Inputs] as React.ComponentType<Record<string, unknown>>;

          const isNumberInput = input.type === 'number';

          return (
            <Input
              // it was added by Huseyin. Also in fileInput.js
              cols={input.cols}
              style={input.style}
              width={input.width}
              key={input.name}
              type={input.type}
              form={props.name}
              label={input.label}
              className={input.class}
              name={input.name}
              value={input.value}
              required={input.required}
              labelTooltip={input.labelTooltip}
              valid={showErrors ? input.valid : undefined}
              min={input.min}
              max={input.max}
              options={input.options}
              selectAll={input.selectAll}
              default={input.default}
              url={input.url}
              text={input.text}
              title={input.title}
              accept={input.accept}
              description={input.description}
              readonly={input.readonly}
              maxFileSize={input.maxFileSize}
              handleLabel={input.handleLabel}
              placeholder={isNumberInput ? undefined : input.placeholder}
              defaultValue={isNumberInput ? (input.defaultValue ?? input.default) : undefined}
              errorMessage={input.errorMessage}
              onChange={update}
              complexPassword={input.complexPassword}
              validateOnBlur={props.validateOnBlur}
            />
          );
        })}
      </Grid>

      {props.preSubmitText && (
        <p className={props.preSubmitClassName ? props.preSubmitClassName : ''} style={props.preSubmitStyle}>
          {props.preSubmitText}
        </p>
      )}
      {props.buttonText && (
        <div style={props.alignRight ? { display: 'flex', justifyContent: 'flex-end' } : undefined}>
          <UMButton
            fullWidth={props.fullWidthButton ?? !props.cancel}
            loading={buttonLoading}
            text={props.buttonText}
            onClick={() => submit()}
            icon={props.icon ? props.icon : ''}
            className={`${props.onboardingStyle ? props.onboardingStyle : shouldUseDefaultButtonClass ? Style.button : ''}${props.buttonClassName ? ` ${props.buttonClassName}` : ''}${
              props.disableModalYesClass ? '' : ' modal-yes-btn'
            }`}
            customClassName={props.customClassName ? props.customClassName : ''}
            color={props.buttonColor}
            variant={props.buttonVariant}
            disabled={props.disabled || buttonLoading}
            style={props.alignRight ? { marginRight: 0 } : undefined}
          />
        </div>
      )}
      {props.cancel && (
        <UMButton
          // color={props.destructive ? 'green' : 'red'}
          // color='blue'
          text="Cancel"
          className={`${Style.button} modal-no-btn`}
          onClick={props.cancel}
        />
      )}
    </form>
  );
}

export { Form };
